import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminUserStatsDto } from 'src/api/FiretrolApiClient';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { formatCurrency, formatDateTime, formatNumber } from 'src/utils';

export interface ITopUsersTableProps {
  users: AdminUserStatsDto[];
  loading: boolean;
}

const getColumns = (): MRT_ColumnDef<AdminUserStatsDto>[] => [
  {
    accessorKey: 'userName',
    header: 'User',
  },
  {
    accessorKey: 'estCostUsd',
    header: 'Est. Cost',
    Cell: ({ cell }) =>
      formatCurrency(cell.getValue<number>(), { precise: true }),
  },
  {
    accessorKey: 'totalTokens',
    header: 'Tokens',
    Cell: ({ cell }) => formatNumber(cell.getValue<number>()),
  },
  {
    accessorKey: 'requestCount',
    header: 'Requests',
    Cell: ({ cell }) => formatNumber(cell.getValue<number>()),
  },
  {
    accessorKey: 'conversationCount',
    header: 'Conversations',
    Cell: ({ cell }) => formatNumber(cell.getValue<number>()),
  },
  {
    accessorKey: 'sqlRejectedCount',
    header: 'Rejected SQL',
    Cell: ({ cell }) => formatNumber(cell.getValue<number>()),
  },
  {
    accessorKey: 'lastActivityAt',
    header: 'Last Activity',
    Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
  },
];

// Top-users list from a single stats/users call — small enough (one row per
// active user in the date range) to sort/filter client-side like the other
// AI Assistant admin tables.
export const TopUsersTable: React.FC<ITopUsersTableProps> = ({
  users,
  loading,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'ai-assistant-top-users',
    columns,
    defaultSorting: [{ id: 'estCostUsd', desc: true }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns,
      data: users,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      mantineTableBodyRowProps: ({ row }) => ({
        onClick: () =>
          navigate(`/settings/ai-assistant/users/${row.original.userId}`),
        style: { cursor: 'pointer' },
      }),
    },
    getRowId: (row) => String(row.userId),
    totalCount: users.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
  });

  return table;
};
